"use client";

import React, { useState, useCallback } from "react";
import TestimonialsCard from "@/components/TestimonialsCard";
import type { Testimonial } from "@/types/cms";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { useSectionTracking } from "@/lib/analytics/use-section-tracking";

const DEFAULT_ITEMS: Testimonial[] = [
  {
    id: 1,
    quote:
      "The connections I made at one dinner led to three enterprise partnerships. This isn't networking — it's strategic relationship building.",
    company: "Nexus AI",
    person: "Marcus Johnson",
    role: "CTO",
    linkedinLink: "https://linkedin.com/in/marcusjohnson",
  },
  {
    id: 2,
    quote:
      "I've attended dozens of tech events. Country Club dinners are different — every person at the table was someone I genuinely wanted to meet.",
    company: "Stripe",
    person: "Sarah Chen",
    role: "VP of Product",
    linkedinLink: "https://linkedin.com/in/sarahchen",
  },
  {
    id: 3,
    quote:
      "The AI matching is remarkable. I sat next to my future co-investor without even knowing we were both looking at the same deal.",
    company: "Velocity Ventures",
    person: "David Miller",
    role: "Founder & CEO",
    linkedinLink: "https://linkedin.com/in/davidmiller",
  },
];

type Props = {
  items?: Testimonial[];
  title?: string;
  highlightedTitle?: string;
  subtitle?: string;
  className?: string;
};

export default function TestimonialsSection({
  items = DEFAULT_ITEMS,
  title = "What",
  highlightedTitle = "People Say",
  subtitle = "Join thousands of executives who have transformed their networks",
  className,
}: Props) {
  const sectionRef = useSectionTracking("Testimonials Section", "testimonials");
  // Start with initial duplicated items
  const [displayedItems, setDisplayedItems] = useState<Testimonial[]>(() =>
    Array(6).fill(items).flat(),
  );

  // Track if we're currently adding items to prevent multiple triggers
  const isAddingRef = React.useRef(false);

  // Handler to add more items when approaching the end
  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      const { activeIndex, slides } = swiper;
      const remainingSlides = slides.length - activeIndex;

      // When 10 or fewer slides remain and we're not already adding
      if (remainingSlides <= 10 && !isAddingRef.current) {
        isAddingRef.current = true;

        // Add more items (3 more copies)
        setDisplayedItems((prev) => {
          const newItems = [...prev, ...items, ...items, ...items];
          // Reset the flag after state update
          setTimeout(() => {
            isAddingRef.current = false;
          }, 100);
          return newItems;
        });
      }
    },
    [items],
  );

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="testimonials"
      className="relative w-full py-20 md:py-32 px-6 bg-black overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            {title} <span className="text-[#D4A84B]">{highlightedTitle}</span>
          </h2>
          <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative">
          <Swiper
            modules={[Autoplay, Navigation]}
            spaceBetween={24}
            slidesPerView={1}
            breakpoints={{
              768: {
                slidesPerView: 3,
                spaceBetween: 32,
              },
            }}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            navigation={{
              nextEl: ".testimonials-button-next",
              prevEl: ".testimonials-button-prev",
            }}
            loop={false}
            onSlideChange={handleSlideChange}
            className="testimonials-swiper"
            style={{
              overflow: "visible",
            }}
          >
            {displayedItems.map((testimonial, index) => (
              <SwiperSlide key={`${testimonial.id}-${index}`}>
                <TestimonialsCard
                  data={testimonial}
                  priority={index === 0}
                  className="h-[500px] md:h-[550px]"
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Navigation Buttons - Overlaid on Cards */}
          <button
            className="testimonials-button-prev absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/30 backdrop-blur-md border border-white/20 flex items-center justify-center text-white transition-all duration-300 hover:bg-black/50 hover:border-[#D4A84B]/50 hover:text-[#D4A84B] hover:scale-110"
            aria-label="Previous testimonial"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            className="testimonials-button-next absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/30 backdrop-blur-md border border-white/20 flex items-center justify-center text-white transition-all duration-300 hover:bg-black/50 hover:border-[#D4A84B]/50 hover:text-[#D4A84B] hover:scale-110"
            aria-label="Next testimonial"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}
